import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { Observer } from 'gsap/Observer';
import { Flip } from 'gsap/Flip';
import { photos, type Photo, type PhotoCategory } from './photos';
import Detail from './Detail';
import TopNav from './TopNav';
import ContentPage from './ContentPage';
import { themes, type ThemeKey } from './themes';

gsap.registerPlugin(Observer, Flip);

// ---- Motion constants (position-based, signed velocity) ----
// Positive velocity = default autoplay direction (right -> left).
// Negative velocity = reversed (left -> right).
const BASE_SPEED = 60;            // px/sec — default autoplay speed
const WHEEL_GAIN = 55;            // impulse per normalized wheel unit
const MAX_FORWARD_SPEED = 180;    // ~3x base
const MAX_REVERSE_SPEED = 180;    // ~3x base, opposite direction
const IMPULSE_DECAY = 2.2;        // per second exponential decay of inputImpulse
const IMPULSE_CLAMP = 260;        // clamp accumulated impulse magnitude
const VELOCITY_SMOOTHING = 6.0;   // per second smoothing toward desired velocity
const RESUME_SMOOTHING = 3.5;     // gentler smoothing after Hover/Detail release
// Snap-to-zero threshold — position-based motion can safely rest exactly at 0.
const VELOCITY_EPSILON = 0.5;

/* eslint-disable no-console */
const dlog = (event: string, data?: Record<string, unknown>) => {
  if (data) console.log(`%c[G] ${event}`, 'color:#0a7', data);
  else console.log(`%c[G] ${event}`, 'color:#0a7');
};
const dwarn = (event: string, data?: Record<string, unknown>) => {
  if (data) console.warn(`[G] ${event}`, data);
  else console.warn(`[G] ${event}`);
};
/* eslint-enable no-console */

type InteractionState =
  | 'gallery'
  | 'hover'
  | 'opening-detail'
  | 'detail'
  | 'closing-detail';

type MotionMode = 'running' | 'stopped';

type TransitionPhase = 'opening' | 'closing';

type Transaction = {
  id: number;
  phase: TransitionPhase;
  timeline: gsap.core.Timeline | null;
  proxy: HTMLDivElement | null;
  originElement: HTMLElement | null;
  originVisual: HTMLElement | null;
  imageId: string;
  cancelled: boolean;
};

export default function Gallery() {
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // ---- Motion engine (position-based, single-writer via ticker) ----
  const motionRef = useRef({
    positionX: 0,                // signed accumulated "default-direction" distance
    copyWidth: 0,                // set by layout effect
    currentVelocity: BASE_SPEED, // signed px/sec
    targetVelocity: BASE_SPEED,  // signed baseline (BASE_SPEED normally, 0 when suppressed)
    inputImpulse: 0,             // signed, decays exponentially
    suppressed: false,           // Hover/Detail requested stop
    resumeMode: false,           // smoother return after release
    mode: 'running' as MotionMode,
    lastTs: 0,
  });

  // ---- Motion API — the ONLY way handlers should influence loop motion ----
  const requestMotionStop = () => {
    const m = motionRef.current;
    m.suppressed = true;
    m.targetVelocity = 0;
    m.inputImpulse = 0;
    m.resumeMode = false;
  };
  const requestMotionResume = () => {
    const m = motionRef.current;
    m.suppressed = false;
    m.targetVelocity = BASE_SPEED;
    m.resumeMode = true;
  };

  // ---- Interaction state machine ----
  const stateRef = useRef<InteractionState>('gallery');

  // ---- Hover ----
  const hoverLayerRef = useRef<HTMLDivElement>(null);
  const hoverSourceRef = useRef<HTMLElement | null>(null);
  const hoverItemRef = useRef<HTMLElement | null>(null);
  const hoverPhotoRef = useRef<Photo | null>(null);
  const hoverProxyRef = useRef<HTMLDivElement | null>(null);
  // Bug 1 fix: after Detail close, if the pointer is still over the returned
  // origin card, suppress Hover on that EXACT card until the pointer
  // genuinely leaves it. Other cards are unaffected.
  const hoverSuppressedItemRef = useRef<HTMLElement | null>(null);
  // During the CloseProxy -> originVisual double-buffered handoff, block all
  // Hover activation for exactly one rAF so a pointerenter can't create a
  // HoverProxy in the ownership swap frame.
  const closeHandoffActiveRef = useRef(false);

  type HoverLeaveSession = {
    id: number;
    ownedVisual: HTMLElement;
    ownedProxy: HTMLDivElement;
    ownedItem: HTMLElement;
    timeline: gsap.core.Timeline | null;
    finalized: boolean;
  };
  const hoverLeaveSessionRef = useRef<HoverLeaveSession | null>(null);
  const hoverLeaveCounter = useRef(0);

  const finalizeHoverLeave = (session: HoverLeaveSession, resumeMotion: boolean) => {
    if (session.finalized) return;
    session.finalized = true;

    if (session.ownedVisual.isConnected) {
      gsap.killTweensOf(session.ownedVisual);
      gsap.set(session.ownedVisual, { autoAlpha: 1, scale: 1, clearProps: 'boxShadow' });
    }
    if (session.ownedProxy) {
      gsap.killTweensOf(session.ownedProxy);
      if (session.ownedProxy.isConnected) session.ownedProxy.remove();
    }
    if (session.timeline) {
      session.timeline.kill();
      session.timeline = null;
    }
    if (hoverLeaveSessionRef.current === session) {
      hoverLeaveSessionRef.current = null;
    }

    if (!resumeMotion) return;

    if (stateRef.current === 'hover' && hoverProxyRef.current === null) {
      stateRef.current = 'gallery';
      requestMotionResume();
    }
  };

  // ---- Detail ----
  const detailRootRef = useRef<HTMLDivElement>(null);
  const detailImageBoxRef = useRef<HTMLDivElement>(null);
  const proxyLayerRef = useRef<HTMLDivElement>(null);
  const detailOriginRef = useRef<HTMLElement | null>(null);

  // ---- Transaction ----
  const transitionRef = useRef<Transaction | null>(null);
  const transitionCounter = useRef(0);

  const [active, setActive] = useState<Photo | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailTextReady, setDetailTextReady] = useState(false);

  // ---- Theme + layout + filter + panel state ----
  const [theme, setTheme] = useState<ThemeKey>('rose');
  const [filter, setFilter] = useState<'All' | PhotoCategory>('All');
  const [contentOpen, setContentOpen] = useState(false);
  const [contentTarget, setContentTarget] = useState<'top' | 'studio' | 'services' | 'contact' | null>(null);
  const layout = 'horizontal' as const;
  const V = themes[theme];
  const filteredPhotos = useMemo(
    () => (filter === 'All' ? photos : photos.filter((p) => p.category === filter)),
    [filter],
  );


  // ------- horizontal loop (position-based; ticker is the sole writer) -------
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    if (layout !== 'horizontal') { motionRef.current.copyWidth = 0; return; }
    const ctx = gsap.context(() => {
      const items = track.querySelectorAll<HTMLElement>('[data-clone-index="0"]');
      let copyWidth = 0;
      items.forEach((el) => { copyWidth += el.offsetWidth; });
      const style = getComputedStyle(track);
      const gap = parseFloat(style.columnGap || style.gap || '0');
      copyWidth += gap * items.length;
      if (copyWidth <= 0) return;

      motionRef.current.copyWidth = copyWidth;
      motionRef.current.positionX = 0;
      gsap.set(track, { x: 0 });

      const gt0 = gsap.globalTimeline as unknown as {
        time: () => number; totalTime: () => number; rawTime: () => number; startTime: () => number;
      };
      dlog('INITIAL CLOCK', {
        perfNow: performance.now(),
        globalTime: gt0.time(),
        globalTotalTime: gt0.totalTime(),
        globalRawTime: gt0.rawTime(),
        globalStartTime: gt0.startTime(),
        copyWidth,
      });
    }, rootRef);
    return () => ctx.revert();
  }, [layout, filter]);

  // ------- ticker (SINGLE WRITER for track.x) -------
  useEffect(() => {
    const wrap = (min: number, max: number, v: number) => {
      const range = max - min;
      if (range <= 0) return min;
      let out = ((v - min) % range + range) % range + min;
      if (out === max) out = min;
      return out;
    };

    const tick = (_time: number, deltaMs: number) => {
      const m = motionRef.current;
      if (m.copyWidth <= 0) return;

      // Delta time in seconds, clamped to survive tab-switch pauses.
      const dt = Math.min(0.1, Math.max(0, deltaMs / 1000));
      if (dt <= 0) return;

      // Impulse decay (exponential).
      if (m.inputImpulse !== 0) {
        const decay = Math.exp(-IMPULSE_DECAY * dt);
        m.inputImpulse *= decay;
        if (Math.abs(m.inputImpulse) < 0.01) m.inputImpulse = 0;
      }

      // Desired velocity: baseline + impulse, unless suppressed.
      let desired = m.suppressed ? 0 : (m.targetVelocity + m.inputImpulse);
      if (desired > MAX_FORWARD_SPEED) desired = MAX_FORWARD_SPEED;
      if (desired < -MAX_REVERSE_SPEED) desired = -MAX_REVERSE_SPEED;

      if (m.mode === 'stopped') {
        // Only wake when there is meaningful demand.
        if (Math.abs(desired) > VELOCITY_EPSILON) {
          m.mode = 'running';
        } else {
          m.currentVelocity = 0;
          return; // no position write
        }
      }

      // Smooth toward desired.
      const k = m.resumeMode ? RESUME_SMOOTHING : VELOCITY_SMOOTHING;
      const alpha = 1 - Math.exp(-k * dt);
      m.currentVelocity += (desired - m.currentVelocity) * alpha;

      if (m.resumeMode && Math.abs(desired - m.currentVelocity) < 0.5) {
        m.resumeMode = false;
      }

      // Snap-to-zero and enter stopped when suppressed and near zero.
      if (m.suppressed && Math.abs(m.currentVelocity) <= VELOCITY_EPSILON) {
        m.currentVelocity = 0;
        m.mode = 'stopped';
        return;
      }

      // Advance position. Positive velocity = default direction (right -> left)
      // means track.x decreases.
      m.positionX += m.currentVelocity * dt;
      const wrapped = wrap(0, m.copyWidth, m.positionX);
      m.positionX = wrapped;

      const track = trackRef.current;
      if (track) {
        gsap.set(track, { x: -wrapped });
      }
    };
    gsap.ticker.add(tick);
    return () => gsap.ticker.remove(tick);
  }, []);

  // ------- clock health monitor (1Hz) -------
  useEffect(() => {
    let last = performance.now();
    const hb = () => {
      const now = performance.now();
      if (now - last < 1000) return;
      last = now;
      const m = motionRef.current;
      const gt = gsap.globalTimeline as unknown as {
        time: () => number; startTime: () => number;
      };
      const gTime = gt.time();
      dlog('MOTION CLOCK', {
        currentVelocity: m.currentVelocity,
        targetVelocity: m.targetVelocity,
        inputImpulse: m.inputImpulse,
        motionMode: m.mode,
        suppressed: m.suppressed,
        positionX: m.positionX,
        globalTime: gTime,
        globalStartTime: gt.startTime(),
      });
      if (Math.abs(gTime) > 100000) {
        // eslint-disable-next-line no-console
        console.error('[G] CLOCK CORRUPTION DETECTED', { globalTime: gTime });
      }
    };
    gsap.ticker.add(hb);
    return () => gsap.ticker.remove(hb);
  }, []);

  // ------- observer (wheel/touch input → signed impulse) -------
  // Suspended while Content World is active so the browser owns wheel input.
  useEffect(() => {
    if (layout !== 'horizontal') return;
    if (contentOpen) return;
    const obs = Observer.create({
      target: rootRef.current!,
      type: 'wheel,touch,pointer',
      preventDefault: true,
      dragMinimum: 3,
      onChange: (self) => {
        const m = motionRef.current;
        if (m.suppressed) return;
        // Sign convention: scroll down / drag left => accelerate default direction (positive).
        const delta = (self.deltaY || 0) - (self.deltaX || 0);
        if (delta === 0) return;
        // Normalize: clamp per-event spikes so trackpads and mice feel similar.
        let normalized = delta / 60;
        if (normalized > 4) normalized = 4;
        if (normalized < -4) normalized = -4;
        m.inputImpulse += normalized * WHEEL_GAIN;
        if (m.inputImpulse > IMPULSE_CLAMP) m.inputImpulse = IMPULSE_CLAMP;
        if (m.inputImpulse < -IMPULSE_CLAMP) m.inputImpulse = -IMPULSE_CLAMP;
      },
    });
    return () => obs.kill();
  }, [layout, contentOpen]);

  // ------- helpers -------
  const preloadImage = (src: string) =>
    new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  const makeProxyDiv = (
    rect: DOMRect,
    src: string,
    radius: string,
    withShadow: boolean,
  ) => {
    const proxy = document.createElement('div');
    proxy.style.cssText = `
      position: fixed;
      top: ${rect.top}px;
      left: ${rect.left}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      border-radius: ${radius};
      overflow: hidden;
      background: #e8e2d6;
      pointer-events: none;
      will-change: transform, border-radius;
      transform-origin: center center;
      ${withShadow ? 'box-shadow: 0 30px 60px -20px rgba(0,0,0,0.28);' : ''}
    `;
    const img = document.createElement('img');
    img.src = src;
    img.draggable = false;
    img.style.cssText =
      'width:100%;height:100%;object-fit:cover;object-position:center;display:block;user-select:none;';
    proxy.appendChild(img);
    return proxy;
  };

  // Build a hover proxy by CLONING the already-rendered source <img> element.
  // The cloned img reuses the browser's already-decoded bitmap so it paints
  // in the same frame as the ownership swap — no decode flash.
  const makeHoverProxyFromSource = (
    rect: DOMRect,
    sourceVisual: HTMLElement,
    radius: string,
  ) => {
    const proxy = document.createElement('div');
    proxy.style.cssText = `
      position: fixed;
      top: ${rect.top}px;
      left: ${rect.left}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      border-radius: ${radius};
      overflow: hidden;
      background: #e8e2d6;
      pointer-events: none;
      will-change: transform, border-radius;
      transform-origin: center center;
      visibility: hidden;
    `;
    const srcImg = sourceVisual.querySelector('img');
    if (srcImg) {
      const clone = srcImg.cloneNode(true) as HTMLImageElement;
      clone.style.cssText =
        'width:100%;height:100%;object-fit:cover;object-position:center;display:block;user-select:none;';
      clone.draggable = false;
      proxy.appendChild(clone);
    }
    return { proxy, isImageReady: isImgDecoded(sourceVisual.querySelector('img')) };
  };

  // Check whether a given <img> has already been decoded and rendered by the
  // browser. If it has, cloning it will paint immediately without a blank frame.
  const isImgDecoded = (img: HTMLImageElement | null): boolean => {
    if (!img) return false;
    return img.complete && img.naturalWidth > 0;
  };

  // Build a close-transition proxy by cloning the already-decoded detail
  // image, avoiding a blank-frame flash from a freshly-created <img>.
  const makeCloseProxyFromDetail = (
    detailRect: DOMRect,
    radius: string,
    detailImgBox: HTMLElement,
  ) => {
    const proxy = document.createElement('div');
    proxy.style.cssText = `
      position: fixed;
      top: ${detailRect.top}px;
      left: ${detailRect.left}px;
      width: ${detailRect.width}px;
      height: ${detailRect.height}px;
      border-radius: ${radius};
      overflow: hidden;
      background: #e8e2d6;
      pointer-events: none;
      will-change: transform;
      transform-origin: center center;
    `;
    const srcImg = detailImgBox.querySelector('img');
    if (srcImg) {
      const clone = srcImg.cloneNode(true) as HTMLImageElement;
      clone.style.cssText =
        'width:100%;height:100%;object-fit:cover;object-position:center;display:block;user-select:none;';
      clone.draggable = false;
      proxy.appendChild(clone);
    }
    return proxy;
  };

  const cancelTransition = (txn: Transaction) => {
    if (txn.cancelled) return;
    txn.cancelled = true;
    if (txn.timeline) {
      txn.timeline.kill();
      txn.timeline = null;
    }
    if (txn.phase === 'opening' && txn.originVisual) {
      gsap.killTweensOf(txn.originVisual);
      gsap.set(txn.originVisual, { autoAlpha: 1 });
    }
    if (txn.proxy) {
      gsap.killTweensOf(txn.proxy);
      if (txn.proxy.isConnected) txn.proxy.remove();
      txn.proxy = null;
    }
  };

  // ---------- HOVER ----------
  // Atomic ownership setup for a new hovered card. Assumes any prior hover
  // proxy/source has already been cleaned up.
  //
  // If the source image is already decoded (common case after first paint),
  // the swap is fully synchronous — proxy paints in the same browser frame
  // as the source is hidden.
  //
  // If the source image has NOT decoded yet (rare: very first hover on a card
  // that just entered the viewport), we keep the source visible, append the
  // proxy hidden, wait for the image to be available, then do the atomic swap
  // in a single rAF so there is never a blank frame.
  const setupHoverOn = (item: HTMLElement, visual: HTMLElement, photo: Photo) => {
    const layer = hoverLayerRef.current;
    if (!layer) return false;
    hoverItemRef.current = item;
    hoverSourceRef.current = visual;
    hoverPhotoRef.current = photo;
    const rect = item.getBoundingClientRect();
    const { proxy, isImageReady } = makeHoverProxyFromSource(rect, visual, '9999px');
    layer.appendChild(proxy);
    hoverProxyRef.current = proxy;

    const commitSwap = () => {
      // Guard: state may have changed (e.g. leave fired) before we committed.
      if (hoverProxyRef.current !== proxy) return;
      // Atomic ownership swap.
      gsap.set(proxy, { autoAlpha: 1, scale: 1, boxShadow: '0 0px 0px 0px rgba(0,0,0,0)' });
      gsap.set(visual, { autoAlpha: 0 });
      // Hover scale/shadow AFTER the swap.
      gsap.to(proxy, {
        scale: 1.05,
        boxShadow: '0 30px 60px -20px rgba(0,0,0,0.28)',
        duration: 0.4,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    };

    if (isImageReady) {
      // Image already decoded — swap in this JS tick, browser paints once.
      commitSwap();
    } else {
      // Image not yet decoded. Keep source visible. Wait for the cloned img
      // inside the proxy to decode (reuses the same network resource so it
      // is usually instant from cache), then swap.
      const cloneImg = proxy.querySelector('img');
      const doSwapAfterDecode = () => {
        // Use rAF to ensure the proxy has been composited before we hide source.
        requestAnimationFrame(() => commitSwap());
      };
      if (cloneImg) {
        if (cloneImg.complete && cloneImg.naturalWidth > 0) {
          doSwapAfterDecode();
        } else if (typeof cloneImg.decode === 'function') {
          cloneImg.decode().then(doSwapAfterDecode).catch(doSwapAfterDecode);
        } else {
          cloneImg.onload = doSwapAfterDecode;
          cloneImg.onerror = doSwapAfterDecode;
        }
      } else {
        commitSwap();
      }
    }
    return true;
  };

  const handleEnter = (e: React.MouseEvent<HTMLElement>, photo: Photo) => {
    const item = e.currentTarget as HTMLElement;
    // Bug 1: suppress hover on the just-returned Detail origin card until
    // the pointer actually leaves it.
    if (hoverSuppressedItemRef.current === item) return;
    if (closeHandoffActiveRef.current) return;
    const visual = item.querySelector<HTMLElement>('.gallery-visual');
    if (!visual) return;

    const s = stateRef.current;

    // Bug 2: A -> B direct hover handoff. Also handles the case where
    // handleLeave already fired for A (state still 'hover' during leave tween).
    if (s === 'hover' || hoverLeaveSessionRef.current) {
      if (hoverItemRef.current === item) return; // re-enter same card

      // Cancel any in-progress leave without resuming motion.
      if (hoverLeaveSessionRef.current) {
        finalizeHoverLeave(hoverLeaveSessionRef.current, false);
      }
      // Tear down the currently-owned hover (A) synchronously.
      const oldProxy = hoverProxyRef.current;
      const oldVisual = hoverSourceRef.current;
      if (oldProxy) {
        gsap.killTweensOf(oldProxy);
        if (oldProxy.isConnected) oldProxy.remove();
      }
      if (oldVisual && oldVisual.isConnected) {
        gsap.killTweensOf(oldVisual);
        gsap.set(oldVisual, { autoAlpha: 1, scale: 1, clearProps: 'boxShadow' });
      }
      hoverProxyRef.current = null;
      hoverItemRef.current = null;
      hoverSourceRef.current = null;
      hoverPhotoRef.current = null;

      // Motion stays stopped. Just set up B.
      stateRef.current = 'hover';
      setupHoverOn(item, visual, photo);
      return;
    }

    if (s !== 'gallery') return;
    if (hoverProxyRef.current) return;

    stateRef.current = 'hover';
    requestMotionStop();
    if (!setupHoverOn(item, visual, photo)) {
      stateRef.current = 'gallery';
      requestMotionResume();
    }
  };

  const handleLeave = () => {
    if (stateRef.current !== 'hover') return;

    const item = hoverItemRef.current;
    const visual = hoverSourceRef.current;
    const proxy = hoverProxyRef.current;
    if (!item || !visual || !proxy) return;

    if (hoverLeaveSessionRef.current) {
      finalizeHoverLeave(hoverLeaveSessionRef.current, false);
    }

    // Keep motion stopped throughout handoff; resume via finalizeHoverLeave.

    const proxyRect = proxy.getBoundingClientRect();
    gsap.killTweensOf(proxy);
    gsap.set(proxy, { clearProps: 'scale' });
    gsap.set(proxy, {
      top: proxyRect.top,
      left: proxyRect.left,
      width: proxyRect.width,
      height: proxyRect.height,
    });

    hoverProxyRef.current = null;
    hoverItemRef.current = null;
    hoverSourceRef.current = null;
    hoverPhotoRef.current = null;

    const session: HoverLeaveSession = {
      id: ++hoverLeaveCounter.current,
      ownedVisual: visual,
      ownedProxy: proxy,
      ownedItem: item,
      timeline: null,
      finalized: false,
    };
    hoverLeaveSessionRef.current = session;

    const tl = gsap.timeline({
      onComplete: () => finalizeHoverLeave(session, true),
    });
    session.timeline = tl;

    const fit = Flip.fit(proxy, item, {
      duration: 0.35,
      ease: 'power2.out',
      borderRadius: '9999px',
    }) as gsap.core.Tween;
    tl.add(fit, 0);
    tl.to(
      proxy,
      { boxShadow: '0 0px 0px 0px rgba(0,0,0,0)', duration: 0.35, ease: 'power2.out' },
      0,
    );
  };

  // ---------- CLICK → DETAIL ----------
  const handleClick = async (photo: Photo, itemEl: HTMLElement) => {
    const s = stateRef.current;
    if (s !== 'gallery' && s !== 'hover') return;

    if (transitionRef.current) {
      cancelTransition(transitionRef.current);
      transitionRef.current = null;
    }
    if (hoverLeaveSessionRef.current) {
      finalizeHoverLeave(hoverLeaveSessionRef.current, false);
    }

    const usingHoverProxy =
      s === 'hover' &&
      hoverItemRef.current === itemEl &&
      hoverProxyRef.current !== null;

    let inheritedProxy: HTMLDivElement | null = null;
    if (usingHoverProxy) {
      inheritedProxy = hoverProxyRef.current!;
      gsap.killTweensOf(inheritedProxy);
      const proxyRect = inheritedProxy.getBoundingClientRect();
      gsap.set(inheritedProxy, {
        clearProps: 'scale,transform',
        top: proxyRect.top,
        left: proxyRect.left,
        width: proxyRect.width,
        height: proxyRect.height,
        x: 0,
        y: 0,
      });
      hoverProxyRef.current = null;
      hoverItemRef.current = null;
      hoverSourceRef.current = null;
      hoverPhotoRef.current = null;
    } else if (s === 'hover') {
      const hp = hoverProxyRef.current;
      const hv = hoverSourceRef.current;
      if (hp) { gsap.killTweensOf(hp); if (hp.isConnected) hp.remove(); }
      if (hv) gsap.set(hv, { autoAlpha: 1 });
      hoverProxyRef.current = null;
      hoverItemRef.current = null;
      hoverSourceRef.current = null;
      hoverPhotoRef.current = null;
    }

    stateRef.current = 'opening-detail';
    requestMotionStop();

    const visual = itemEl.querySelector<HTMLElement>('.gallery-visual');
    detailOriginRef.current = itemEl;

    const txn: Transaction = {
      id: ++transitionCounter.current,
      phase: 'opening',
      timeline: null,
      proxy: inheritedProxy,
      originElement: itemEl,
      originVisual: visual,
      imageId: photo.id,
      cancelled: false,
    };
    transitionRef.current = txn;

    await Promise.all([
      preloadImage(photo.src),
      new Promise<void>((r) => setTimeout(r, 150)),
    ]);
    if (txn.cancelled || transitionRef.current !== txn) {
      if (txn.proxy?.isConnected) txn.proxy.remove();
      return;
    }

    if (visual) gsap.set(visual, { autoAlpha: 0 });

    // Reset any lingering hidden state on the detail image box from a prior
    // close before Detail root becomes visible.
    if (detailImageBoxRef.current) {
      gsap.set(detailImageBoxRef.current, { autoAlpha: 1, clearProps: 'opacity,visibility' });
    }

    setActive(photo);
    setDetailVisible(true);
    await new Promise<void>((r) =>
      requestAnimationFrame(() => requestAnimationFrame(() => r())),
    );
    if (txn.cancelled || transitionRef.current !== txn) {
      if (txn.proxy?.isConnected) txn.proxy.remove();
      if (visual) gsap.set(visual, { autoAlpha: 1 });
      return;
    }

    const targetEl = detailImageBoxRef.current;
    const proxyLayer = proxyLayerRef.current;
    if (!targetEl || !proxyLayer) return;

    let proxy = txn.proxy;
    if (!proxy) {
      const rect = itemEl.getBoundingClientRect();
      proxy = makeProxyDiv(rect, photo.src, '9999px', false);
      proxyLayer.appendChild(proxy);
      txn.proxy = proxy;
    } else if (proxy.parentNode !== proxyLayer) {
      proxyLayer.appendChild(proxy);
    }

    gsap.set(targetEl, { autoAlpha: 0 });

    let watchdogId: ReturnType<typeof setTimeout> | null = null;
    const clearWatchdog = () => {
      if (watchdogId !== null) { clearTimeout(watchdogId); watchdogId = null; }
    };
    const rollbackOpening = (reason: string) => {
      clearWatchdog();
      if (txn.cancelled || transitionRef.current !== txn) return;
      dwarn('opening rollback', { reason });
      txn.cancelled = true;
      if (txn.timeline) { txn.timeline.kill(); txn.timeline = null; }
      if (txn.proxy?.isConnected) txn.proxy.remove();
      txn.proxy = null;
      if (txn.originVisual && txn.originVisual.isConnected) {
        gsap.killTweensOf(txn.originVisual);
        gsap.set(txn.originVisual, { autoAlpha: 1, scale: 1, clearProps: 'boxShadow' });
      }
      setDetailTextReady(false);
      setDetailVisible(false);
      setActive(null);
      if (detailRootRef.current) gsap.set(detailRootRef.current, { opacity: 1 });
      gsap.set(targetEl, { autoAlpha: 1 });
      detailOriginRef.current = null;
      transitionRef.current = null;
      stateRef.current = 'gallery';
      requestMotionResume();
    };

    try {
      const tl = gsap.timeline({
        onComplete: () => {
          clearWatchdog();
          if (txn.cancelled || transitionRef.current !== txn) {
            if (txn.proxy?.isConnected) txn.proxy.remove();
            txn.proxy = null;
            txn.timeline = null;
            return;
          }
          gsap.set(targetEl, { autoAlpha: 1 });
          if (txn.proxy?.isConnected) txn.proxy.remove();
          txn.proxy = null;
          txn.timeline = null;
          transitionRef.current = null;
          stateRef.current = 'detail';
        },
      });
      txn.timeline = tl;

      const flipTween = Flip.fit(proxy, targetEl, {
        duration: 0.82,
        ease: 'power3.inOut',
        borderRadius: '24px',
      }) as gsap.core.Tween;
      if (!flipTween) throw new Error('Flip.fit returned null');
      tl.add(flipTween, 0);
      tl.call(
        () => {
          if (txn.cancelled || transitionRef.current !== txn) return;
          setDetailTextReady(true);
        },
        undefined,
        0.5,
      );

      watchdogId = setTimeout(() => {
        watchdogId = null;
        if (txn.cancelled || transitionRef.current !== txn) return;
        if (stateRef.current === 'opening-detail') {
          rollbackOpening('watchdog timeout');
        }
      }, 2000);
    } catch (err) {
      rollbackOpening(`exception: ${(err as Error)?.message ?? String(err)}`);
    }
  };

  // ---------- BACK ----------
  // Optional resolver fires exactly once when the close sequence has fully
  // committed (proxy removed, gallery restored, state = 'gallery').
  const closeResolveRef = useRef<(() => void) | null>(null);
  const flushCloseResolve = () => {
    const r = closeResolveRef.current;
    closeResolveRef.current = null;
    if (r) r();
  };
  const chainLockRef = useRef(false);

  const handleBack = () => {
    if (stateRef.current !== 'detail') {
      dwarn('BACK rejected — state not detail', { stateRef: stateRef.current });
      flushCloseResolve();
      return;
    }
    if (!active) {
      dwarn('BACK rejected — no active');
      flushCloseResolve();
      return;
    }
    if (transitionRef.current) {
      cancelTransition(transitionRef.current);
      transitionRef.current = null;
    }

    const photo = active;
    const itemEl = detailOriginRef.current;
    const targetEl = detailImageBoxRef.current;
    const proxyLayer = proxyLayerRef.current;

    if (!itemEl || !targetEl || !proxyLayer || !itemEl.isConnected) {
      dwarn('BACK safety teardown — missing refs');
      setDetailTextReady(false);
      setDetailVisible(false);
      setActive(null);
      detailOriginRef.current = null;
      stateRef.current = 'gallery';
      requestMotionResume();
      flushCloseResolve();
      return;
    }

    stateRef.current = 'closing-detail';
    setDetailTextReady(false);

    // Bug 1: arm hover suppression on the returning card. Cleared on genuine leave.
    hoverSuppressedItemRef.current = itemEl;

    // Motion stop via central API — no direct timeScale(0)/pause() writes here.
    requestMotionStop();

    const destRect = itemEl.getBoundingClientRect();
    const detailRect = targetEl.getBoundingClientRect();

    const geomValid =
      Number.isFinite(destRect.left) && Number.isFinite(destRect.top) &&
      destRect.width > 0 && destRect.height > 0 &&
      Number.isFinite(detailRect.left) && Number.isFinite(detailRect.top) &&
      detailRect.width > 0 && detailRect.height > 0;

    if (!geomValid) {
      dwarn('CLOSE aborted — invalid geometry, running safety teardown');
      setDetailTextReady(false);
      setDetailVisible(false);
      setActive(null);
      const ov = itemEl.querySelector<HTMLElement>('.gallery-visual');
      if (ov) gsap.set(ov, { autoAlpha: 1 });
      gsap.set(targetEl, { autoAlpha: 1 });
      detailOriginRef.current = null;
      stateRef.current = 'gallery';
      requestMotionResume();
      flushCloseResolve();
      return;
    }

    const proxy = makeCloseProxyFromDetail(detailRect, '24px', targetEl);
    proxyLayer.appendChild(proxy);

    const originVisual = itemEl.querySelector<HTMLElement>('.gallery-visual');
    if (originVisual) gsap.set(originVisual, { autoAlpha: 0 });

    const txn: Transaction = {
      id: ++transitionCounter.current,
      phase: 'closing',
      timeline: null,
      proxy,
      originElement: itemEl,
      originVisual,
      imageId: photo.id,
      cancelled: false,
    };
    transitionRef.current = txn;

    gsap.set(targetEl, { autoAlpha: 0 });

    let closeWatchdogId: ReturnType<typeof setTimeout> | null = null;
    const clearCloseWatchdog = () => {
      if (closeWatchdogId !== null) { clearTimeout(closeWatchdogId); closeWatchdogId = null; }
    };
    const rollbackClosing = (reason: string) => {
      clearCloseWatchdog();
      if (txn.cancelled || transitionRef.current !== txn) return;
      dwarn('CLOSE ROLLBACK', { reason });
      txn.cancelled = true;
      if (txn.timeline) { txn.timeline.kill(); txn.timeline = null; }
      if (txn.proxy?.isConnected) txn.proxy.remove();
      txn.proxy = null;
      if (txn.originVisual?.isConnected) {
        gsap.killTweensOf(txn.originVisual);
        gsap.set(txn.originVisual, { autoAlpha: 1, scale: 1, clearProps: 'boxShadow' });
      }
      gsap.set(targetEl, { autoAlpha: 1 });
      setDetailVisible(false);
      setActive(null);
      detailOriginRef.current = null;
      transitionRef.current = null;
      stateRef.current = 'gallery';
      requestMotionResume();
      flushCloseResolve();
    };

    // Direct standalone close tween (proven-working path).
    const closeTween = gsap.to(proxy, {
      top: destRect.top,
      left: destRect.left,
      width: destRect.width,
      height: destRect.height,
      borderRadius: '9999px',
      duration: 0.78,
      ease: 'power3.inOut',
      onComplete: () => {
        clearCloseWatchdog();
        if (txn.cancelled || transitionRef.current !== txn) {
          if (txn.proxy?.isConnected) txn.proxy.remove();
          txn.proxy = null;
          txn.timeline = null;
          return;
        }

        const proxyEl = txn.proxy;
        const ov = txn.originVisual;

        // ---- Double-buffered handoff: CloseProxy -> real gallery visual ----
        // Block hover during the buffer frame.
        closeHandoffActiveRef.current = true;

        // Detail image (targetEl) stays hidden throughout — reset only on next open.
        setDetailVisible(false);
        setActive(null);

        if (proxyEl && ov && ov.isConnected && proxyEl.isConnected) {
          // Re-measure the CURRENT origin visual rect at commit time. The
          // gallery may have shifted a hair while the close tween ran.
          const finalRect = ov.getBoundingClientRect();
          const proxyRect = proxyEl.getBoundingClientRect();
          dlog('CLOSE HANDOFF DELTA', {
            dx: proxyRect.left - finalRect.left,
            dy: proxyRect.top - finalRect.top,
            dw: proxyRect.width - finalRect.width,
            dh: proxyRect.height - finalRect.height,
          });
          // Snap proxy exactly onto the origin visual — no animation.
          gsap.killTweensOf(proxyEl);
          gsap.set(proxyEl, {
            clearProps: 'transform,scale,x,y',
          });
          gsap.set(proxyEl, {
            top: finalRect.top,
            left: finalRect.left,
            width: finalRect.width,
            height: finalRect.height,
            borderRadius: '9999px',
          });
          // Reveal the real gallery visual UNDERNEATH the proxy.
          gsap.killTweensOf(ov);
          gsap.set(ov, { autoAlpha: 1, scale: 1, clearProps: 'boxShadow' });

          // Wait exactly one rAF, then remove the proxy — the real visual
          // has already been painted underneath.
          requestAnimationFrame(() => {
            if (transitionRef.current === txn && proxyEl.isConnected) {
              proxyEl.remove();
            } else if (proxyEl.isConnected) {
              proxyEl.remove();
            }
            txn.proxy = null;
            txn.timeline = null;
            detailOriginRef.current = null;
            if (transitionRef.current === txn) transitionRef.current = null;
            stateRef.current = 'gallery';
            closeHandoffActiveRef.current = false;
            requestMotionResume();
            flushCloseResolve();
          });
          return;
        }

        // Fallback (missing refs): behave like before.
        if (ov?.isConnected) {
          gsap.killTweensOf(ov);
          gsap.set(ov, { autoAlpha: 1, scale: 1, clearProps: 'boxShadow' });
        }
        if (proxyEl?.isConnected) proxyEl.remove();
        txn.proxy = null;
        txn.timeline = null;
        detailOriginRef.current = null;
        transitionRef.current = null;
        stateRef.current = 'gallery';
        closeHandoffActiveRef.current = false;
        requestMotionResume();
        flushCloseResolve();
      },
    });
    txn.timeline = closeTween as unknown as gsap.core.Timeline;

    closeWatchdogId = setTimeout(() => {
      closeWatchdogId = null;
      if (txn.cancelled || transitionRef.current !== txn) return;
      if (stateRef.current === 'closing-detail') rollbackClosing('watchdog timeout');
    }, 2000);
  };

  // ---------- render ----------
  const rendered: Array<{ photo: Photo; copy: number }> = [];
  [0, 1].forEach((copy) => {
    filteredPhotos.forEach((p) => rendered.push({ photo: p, copy }));
  });

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 overflow-hidden select-none"
      style={{ touchAction: 'none', background: V.bg }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: V.radial }}
      />

      <TopNav
        theme={theme}
        onThemeChange={setTheme}
        onNavigate={(p) => { setContentTarget(p); setContentOpen(true); }}
        topText={V.topText}
        filter={filter}
        onFilterChange={setFilter}
      />

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 z-40 flex items-center justify-between px-8 py-5 pointer-events-none">
        <span className="text-[11px] tracking-[0.3em] uppercase text-white/60">
          Scroll · Drag · Hover
        </span>
        <span className="text-[11px] tracking-[0.3em] uppercase text-white/60">
          {String(filteredPhotos.length).padStart(2, '0')} / {String(photos.length).padStart(2, '0')} works
        </span>
      </div>

      <div
        ref={trackRef}
        className="absolute left-0 will-change-transform"
        style={{
          top: '50%',
          marginTop: '-180px',
          display: 'flex',
          gap: '48px',
          paddingLeft: '48px',
          zIndex: 10,
        }}
      >
        {rendered.map(({ photo, copy }, i) => (
          <div
            key={`${photo.id}-${copy}-${i}`}
            data-image-id={photo.id}
            data-clone-index={copy}
            onMouseEnter={(e) => handleEnter(e, photo)}
            onMouseLeave={(e) => {
              if (hoverSuppressedItemRef.current === e.currentTarget) {
                hoverSuppressedItemRef.current = null;
              }
              handleLeave();
            }}
            onClick={(e) => handleClick(photo, e.currentTarget)}
            className="gallery-item relative shrink-0 cursor-pointer"
            style={{ width: '240px', height: '360px' }}
          >
            <div
              className="gallery-visual absolute inset-0"
              style={{
                borderRadius: '9999px',
                overflow: 'hidden',
                background: V.itemBg,
              }}
            >
              <img
                src={photo.src}
                alt={photo.title}
                draggable={false}
                className="w-full h-full pointer-events-none"
                style={{ objectFit: 'cover', objectPosition: 'center', display: 'block' }}
              />
            </div>
          </div>
        ))}
      </div>

      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none text-center"
        style={{ zIndex: 20 }}
      >
        <div className="font-logo-b text-[140px] md:text-[200px] leading-[0.9] text-white">
          Lumen
        </div>
        <div className="mt-4 text-[10px] tracking-[0.5em] uppercase text-white/85">
          a photography studio
        </div>
      </div>

      <div
        ref={hoverLayerRef}
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 30 }}
      />

      <Detail
        ref={detailRootRef}
        imageBoxRef={detailImageBoxRef}
        photo={active}
        visible={detailVisible}
        textReady={detailTextReady}
        onBack={handleBack}
        onOpenServices={() => {
          if (chainLockRef.current) return;
          if (stateRef.current !== 'detail') return;
          chainLockRef.current = true;
          const closeDetailCompletely = () => new Promise<void>((resolve) => {
            closeResolveRef.current = resolve;
            handleBack();
          });
          const nextPaint = () => new Promise<void>((r) =>
            requestAnimationFrame(() => requestAnimationFrame(() => r())),
          );
          const enterContentWorld = () => new Promise<void>((resolve) => {
            setContentTarget('services');
            setContentOpen(true);
            // The Content surface transition is 0.9s; wait for it to settle
            // before releasing the chain lock. Scroll to #services is handled
            // by ContentPage's own post-open timer.
            setTimeout(resolve, 950);
          });
          (async () => {
            await closeDetailCompletely();
            await nextPaint();
            await enterContentWorld();
            chainLockRef.current = false;
          })();
        }}
        theme={theme}
      />

      <div
        ref={proxyLayerRef}
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 100 }}
      />

      <ContentPage
        open={contentOpen}
        onClose={() => setContentOpen(false)}
        onOpen={() => { setContentTarget('top'); setContentOpen(true); }}
        theme={theme}
        scrollTarget={contentTarget}
        onScrollHandled={() => setContentTarget(null)}
      />
    </div>
  );
}